import { TUmbracoContentItem } from "./types";

type TUmbracoContentType = TUmbracoContentItem["type"];

type TSpacingRule = {
  from: (type: TUmbracoContentType) => boolean;
  to: (type: TUmbracoContentType) => boolean;
  marginBottom: number;
};

const isText = (type: TUmbracoContentType) => type === "text" || type === "smallText";
const isDate = (type: TUmbracoContentType) => type === "dateText";
const isTitle = (type: TUmbracoContentType) => type === "title";
const isSubtitle = (type: TUmbracoContentType) => type === "subtitle";
const isTitleOrSubtitle = (type: TUmbracoContentType) => isTitle(type) || isSubtitle(type);
const isButton = (type: TUmbracoContentType) => type === "largeButton" || type === "smallButton";
const isSmallButton = (type: TUmbracoContentType) => type === "smallButton";
const isLargeButton = (type: TUmbracoContentType) => type === "largeButton";
const isBullet = (type: TUmbracoContentType) => type === "bulletList";
const isImage = (type: TUmbracoContentType) => type === "image";
const isDocument = (type: TUmbracoContentType) => type === "documentList";
const isHighlighted = (type: TUmbracoContentType) => type === "highlightedText";
const isAccordion = (type: TUmbracoContentType) => type === "accordionList";
const isContactCard = (type: TUmbracoContentType) => type === "contactCard";

const spacingRules: TSpacingRule[] = [
  { from: isTitle, to: isSubtitle, marginBottom: 8 },
  { from: isTitleOrSubtitle, to: isText, marginBottom: 16 },
  { from: isText, to: isTitleOrSubtitle, marginBottom: 32 },
  { from: isBullet, to: isBullet, marginBottom: 16 },
  { from: isBullet, to: isText, marginBottom: 16 },
  { from: isBullet, to: isTitleOrSubtitle, marginBottom: 32 },
  { from: () => true, to: isContactCard, marginBottom: 48 },
  { from: isContactCard, to: () => true, marginBottom: 48 },
  { from: isDate, to: () => true, marginBottom: 8 },
  { from: () => true, to: isTitle, marginBottom: 32 },
  { from: isLargeButton, to: (type) => !isLargeButton(type), marginBottom: 40 },
  { from: (type) => !isLargeButton(type), to: isLargeButton, marginBottom: 40 },
  { from: isSmallButton, to: (type) => !isLargeButton(type), marginBottom: 24 },
  { from: (type) => !isLargeButton(type), to: isSmallButton, marginBottom: 24 },
  { from: isSmallButton, to: isLargeButton, marginBottom: 40 },
  { from: isLargeButton, to: isSmallButton, marginBottom: 40 },
  { from: isHighlighted, to: () => true, marginBottom: 24 },
  { from: isImage, to: isTitle, marginBottom: 32 },
  { from: isImage, to: isButton, marginBottom: 40 },
  { from: isImage, to: () => true, marginBottom: 24 },
  { from: isTitle, to: isImage, marginBottom: 32 },
  { from: isButton, to: isImage, marginBottom: 40 },
  { from: () => true, to: isImage, marginBottom: 24 },
  { from: isDocument, to: isTitle, marginBottom: 32 },
  { from: isDocument, to: isButton, marginBottom: 40 },
  { from: isTitle, to: isDocument, marginBottom: 32 },
  { from: isButton, to: isDocument, marginBottom: 40 },
  { from: isAccordion, to: () => true, marginBottom: 32 },
  { from: () => true, to: isAccordion, marginBottom: 32 },
  { from: isText, to: isText, marginBottom: 16 },
  { from: () => true, to: () => true, marginBottom: 8 },
];

export const getMarginBetweenComponents = (
  item: TUmbracoContentItem,
  nextItem?: TUmbracoContentItem,
) => {
  if (!nextItem) return 0;

  return (
    spacingRules.find(({ from, to }) => from(item.type) && to(nextItem.type))?.marginBottom ?? 0
  );
};
